import { type Action, createStore, type Store as StoreType } from 'react-sweet-state';

/** Apply state for a single plan step. */
export type ActionStepApplyEntry = {
	inProgress: boolean;
	error: Error | undefined;
};

/** Default entry for a `(conversationId, invocationId)` pair that has never been touched. */
const DEFAULT_ENTRY: ActionStepApplyEntry = { inProgress: false, error: undefined };

/**
 * Global store state, nested as `conversationId -> invocationId -> entry`.
 *
 * Keyed by `conversationId` (not just `invocationId`) to defensively scope entries per
 * conversation, matching the `useActionInvocationStatuses(conversationId, invocationIds)`
 * convention, rather than assuming `invocationId` is globally unique across conversations.
 *
 * Independent of the canvas-instance-keyed `RovodalStore`: the inline
 * `JiraAdminAgentActionPlanCard` mounts before the canvas is ever opened, so this state cannot
 * live in a store keyed off canvas open/close lifecycle.
 */
export type ActionStepApplyStoreState = {
	byConversation: Record<string, Record<string, ActionStepApplyEntry>>;
};

const initialState: ActionStepApplyStoreState = {
	byConversation: {},
};

export type ActionStepApplyActions = {
	/** Mark the `(conversationId, invocationId)` step as in-progress (or not). */
	setInProgress: (
		conversationId: string,
		invocationId: string,
		inProgress: boolean,
	) => Action<ActionStepApplyStoreState>;
	/** Set (or clear, via `undefined`) the error for the `(conversationId, invocationId)` step. */
	setError: (
		conversationId: string,
		invocationId: string,
		error: Error | undefined,
	) => Action<ActionStepApplyStoreState>;
};

/** Immutably write a single `invocationId` entry into the nested `byConversation` map. */
const writeEntry = (
	byConversation: ActionStepApplyStoreState['byConversation'],
	conversationId: string,
	invocationId: string,
	entry: ActionStepApplyEntry,
): ActionStepApplyStoreState['byConversation'] => ({
	...byConversation,
	[conversationId]: {
		...byConversation[conversationId],
		[invocationId]: entry,
	},
});

const actions: ActionStepApplyActions = {
	setInProgress:
		(conversationId, invocationId, inProgress) =>
		({ setState, getState }) => {
			const byConversation = getState().byConversation;
			const existing = byConversation[conversationId]?.[invocationId] ?? DEFAULT_ENTRY;
			setState({
				byConversation: writeEntry(byConversation, conversationId, invocationId, {
					...existing,
					inProgress,
				}),
			});
		},

	setError:
		(conversationId, invocationId, error) =>
		({ setState, getState }) => {
			const byConversation = getState().byConversation;
			const existing = byConversation[conversationId]?.[invocationId] ?? DEFAULT_ENTRY;
			setState({
				byConversation: writeEntry(byConversation, conversationId, invocationId, {
					...existing,
					error,
				}),
			});
		},
};

/**
 * The single, private action-step-apply store singleton. Public hooks in sibling files are
 * created from this; it is not exposed via the package `exports`.
 */
export const ActionStepApplyStore: StoreType<ActionStepApplyStoreState, ActionStepApplyActions> =
	createStore({
		name: 'action-step-apply',
		initialState,
		actions,
	});
